// stores/mapStore.ts
import type { MapAction, MapComponentState } from '$lib/utils/map'
import { writable, derived, type Readable } from 'svelte/store'

function createMapStore() {
	const initialState: MapComponentState = {
		map: null,
		isLoaded: false,
		isDragging: false,
		isMoving: false,
		zoom: 2,
		bounds: null,
		center: null,
		isProcessingBounds: false,
		isMobileScreen: false,
	}

	const { subscribe, update, set } = writable<MapComponentState>(initialState)

	function reducer(state: MapComponentState, action: MapAction): MapComponentState {
		switch (action.type) {
			case 'SET_MAP':
				return { ...state, map: action.payload }
			case 'SET_IS_MOBILE_SCREEN':
				return { ...state, isMobileScreen: action.payload }
			case 'SET_LOADED':
				return { ...state, isLoaded: action.payload }
			case 'SET_DRAGGING':
				return { ...state, isDragging: action.payload }
			case 'SET_MOVING':
				return { ...state, isMoving: action.payload }
			case 'SET_ZOOM':
				return { ...state, zoom: action.payload }
			case 'SET_BOUNDS':
				return { ...state, bounds: action.payload }
			case 'SET_CENTER':
				return { ...state, center: action.payload }
			case 'SET_PROCESSING_BOUNDS':
				return { ...state, isProcessingBounds: action.payload }
			case 'RESET':
				return initialState
			default:
				return state
		}
	}

	return {
		subscribe,
		dispatch: (action: MapAction) => update((state) => reducer(state, action)),
		reset: () => set(initialState),
	}
}

export const mapStore = createMapStore()

// Derived stores for commonly used values
export const mapInstance: Readable<google.maps.Map | null> = derived(
	mapStore,
	($store) => $store.map,
)

export const isMapLoaded: Readable<boolean> = derived(mapStore, ($store) => $store.isLoaded)

export const mapZoom: Readable<number> = derived(mapStore, ($store) => $store.zoom)

export const isDragging: Readable<boolean> = derived(mapStore, ($store) => $store.isDragging)

export const isMoving: Readable<boolean> = derived(mapStore, ($store) => $store.isMoving)

export const isProcessingBounds: Readable<boolean> = derived(
	mapStore,
	($store) => $store.isProcessingBounds,
)

export const mapCenter: Readable<google.maps.LatLngLiteral | null> = derived(mapStore, ($store) => {
	if (!$store.map) return null
	const center = $store.map.getCenter()?.toJSON()
	return center || null
})
